import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TSimpleCase } from './types/case.type';

export type TGetListCaseResponse = ApiResponse<IPagination<TSimpleCase>>;

export const getListCase = async (params: FetchParams): Promise<TGetListCaseResponse> => {
  const url = `case`;

  return axiosInstance.get(url, { params });
};

/**
 * Use Get List Case React-Query Hook
 */
export const GET_LIST_CASE_QUERY_KEY = 'get-list-case';

export const getListCaseQueryOptions = <TData = TGetListCaseResponse>(
  params: FetchParams,
  select?: (data: TGetListCaseResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_CASE_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListCase(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListCaseQuery<TData>(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListCaseQueryOptions<TData>>;
  select: (data: TGetListCaseResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListCaseQuery(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListCaseQueryOptions<TGetListCaseResponse>>;
}): ReturnType<typeof useQuery<TGetListCaseResponse>>;

// Implementation
export function useListCaseQuery<TData = TGetListCaseResponse>({
  params,
  queryConfig,
  select,
}: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListCaseQueryOptions<TData | TGetListCaseResponse>>;
  select?: (data: TGetListCaseResponse) => TData;
}) {
  return useQuery({
    ...getListCaseQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListCaseResponse ? TGetListCaseResponse : TData>>;
}
